import type { InterviewType } from "@/services/interview.service";

// ─── Time ─────────────────────────────────────────────────────────────────────

/**
 * The backend guarantees UTC with a trailing `Z`, but Pydantic passes tzinfo
 * through rather than normalising it. A suffix-less string would be parsed as
 * browser-local time and show the student the wrong hour, so we append `Z`
 * whenever no zone is present.
 */
export const parseUtc = (iso: string): Date => {
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
};

const dateFmt = new Intl.DateTimeFormat("en-IN", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

const timeFmt = new Intl.DateTimeFormat("en-IN", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/** e.g. "Tue, 22 Sep 2026" — rendered in the viewer's own timezone. */
export const formatInterviewDate = (iso: string): string =>
  dateFmt.format(parseUtc(iso));

/** The short zone label so a student travelling/abroad isn't misled. */
export const localTimeZoneLabel = (): string =>
  new Intl.DateTimeFormat("en-IN", { timeZoneName: "short" })
    .formatToParts(new Date())
    .find((part) => part.type === "timeZoneName")?.value ?? "";

/**
 * e.g. "10:30 AM – 12:00 PM GMT+5:30".
 * The API never sends an end time — it is derived from duration_minutes.
 */
export const formatInterviewTimeRange = (
  iso: string,
  durationMinutes: number
): string => {
  const start = parseUtc(iso);
  const startLabel = timeFmt.format(start);
  const zone = localTimeZoneLabel();

  if (!durationMinutes || durationMinutes <= 0) {
    return zone ? `${startLabel} ${zone}` : startLabel;
  }

  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const range = `${startLabel} – ${timeFmt.format(end)}`;
  return zone ? `${range} ${zone}` : range;
};

export const formatDuration = (minutes: number): string => {
  if (!minutes || minutes <= 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

/** "Today" / "Tomorrow" / "In 4 days" — only meaningful for upcoming rounds. */
export const relativeDayLabel = (iso: string): string | null => {
  const target = parseUtc(iso);
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round(
    (startOfDay(target) - startOfDay(new Date())) / 86_400_000
  );

  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days > 1 && days <= 14) return `In ${days} days`;
  return null;
};

// ─── Labels ───────────────────────────────────────────────────────────────────

const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  technical: "Technical Round",
  hr: "HR Round",
  in_person: "In Person",
  phone: "Phone Screen",
  video: "Video Call",
};

/** Falls back to Title Case so a newly added backend type never renders raw. */
export const interviewTypeLabel = (type: string): string =>
  INTERVIEW_TYPE_LABELS[type] ??
  type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

// ─── Links ────────────────────────────────────────────────────────────────────

/**
 * meeting_link is unvalidated recruiter free text — it can hold a room code, a
 * phone number, or anything else typed into the form. Only an http(s) URL is
 * safe to place in an anchor's href, so everything else is returned as plain
 * text for display instead.
 */
export const safeMeetingLink = (
  link: string | null
): { href: string } | { text: string } | null => {
  const trimmed = link?.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? { href: trimmed } : { text: trimmed };
};

// ─── Derived display state ────────────────────────────────────────────────────

export type InterviewDisplayState =
  | "upcoming"
  | "awaiting_update"
  | "completed"
  | "cancelled"
  | "no_show";

/**
 * `scheduled` on its own is not enough to render a card: the backend runs no
 * job to age interviews out, so a round the recruiter never marked stays
 * `scheduled` forever and would otherwise look live long after it happened.
 */
export const interviewDisplayState = (
  interview: Pick<InterviewType, "status" | "scheduled_at">
): InterviewDisplayState => {
  switch (interview.status) {
    case "cancelled":
      return "cancelled";
    case "no_show":
      return "no_show";
    case "completed":
      return "completed";
    default:
      return parseUtc(interview.scheduled_at).getTime() < Date.now()
        ? "awaiting_update"
        : "upcoming";
  }
};

export interface InterviewStateMeta {
  label: string;
  /** Shown inside the card body; null when no explanation is needed. */
  note: string | null;
  chip: string;
  dot: string;
  accent: string;
  muted: boolean;
}

export const INTERVIEW_STATE_META: Record<
  InterviewDisplayState,
  InterviewStateMeta
> = {
  upcoming: {
    label: "Scheduled",
    note: null,
    chip: "text-emerald-700 bg-emerald-50 border-emerald-100",
    dot: "bg-emerald-500",
    accent: "bg-gradient-to-r from-emerald-400 to-teal-500",
    muted: false,
  },
  awaiting_update: {
    label: "Awaiting update",
    note: "This round's scheduled time has passed. Your recruiter hasn't recorded the outcome yet.",
    chip: "text-amber-700 bg-amber-50 border-amber-100",
    dot: "bg-amber-500",
    accent: "bg-gradient-to-r from-amber-300 to-orange-400",
    muted: false,
  },
  completed: {
    label: "Completed",
    note: null,
    chip: "text-indigo-700 bg-indigo-50 border-indigo-100",
    dot: "bg-indigo-500",
    accent: "bg-gradient-to-r from-indigo-400 to-violet-500",
    muted: false,
  },
  cancelled: {
    label: "Cancelled",
    note: "The recruiter cancelled this interview. Contact your placement office for details.",
    chip: "text-rose-700 bg-rose-50 border-rose-100",
    dot: "bg-rose-400",
    accent: "bg-gradient-to-r from-rose-300 to-red-400",
    muted: true,
  },
  // Deliberately neutral: the API never says who failed to attend.
  no_show: {
    label: "Did not take place",
    note: "This round was not held.",
    chip: "text-slate-600 bg-slate-100 border-slate-200",
    dot: "bg-slate-400",
    accent: "bg-gradient-to-r from-slate-300 to-slate-400",
    muted: true,
  },
};
